import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { monthlyClientDashboard } from 'src/api/reports';
import { getClientProperties } from 'src/api/ShiftMaster';
import { getClientGroupInfo } from 'src/api/liveCameras';
import FormComponent from 'src/templates/MonthlyClientTemplate';
import { getAppStorageItem } from 'src/utils/storageProxy';

const currentYear = new Date().getFullYear();

const months = Array.from({ length: 12 }, (_, idx) => {
  const date = new Date(currentYear, idx, 1);
  return {
    name: date.toLocaleString('default', { month: 'long' }),
    value: String(idx + 1),
  };
});

const years = Array.from({ length: 2 }, (_, idx) => {
  const year = currentYear - 1 + idx;
  return { name: String(year), value: year };
});

const getAssignedLocationsFromStorage = () => {
  try {
    const assignedLocationsRaw = getAppStorageItem('assigned_locations');
    if (!assignedLocationsRaw) return [];

    const parsedAssignedLocations = JSON.parse(assignedLocationsRaw);
    const assignedLocations = Array.isArray(parsedAssignedLocations)
      ? parsedAssignedLocations
      : [];

    return assignedLocations
      .map((locationItem) => ({
        ID: Number(locationItem?.ID ?? locationItem?.id ?? 0),
        Location: String(locationItem?.Location || locationItem?.name || '').trim(),
      }))
      .filter((locationItem) => locationItem.ID > 0);
  } catch (error) {
    console.error('Failed to read assigned_locations from storage:', error);
    return [];
  }
};

export default function MonthlyClientDashBoard() {
  const currentClientId = getAppStorageItem('client_id') || '';
  const currentClientName = getAppStorageItem('displayname') || '';
  const storedProperty = getAppStorageItem('Location');
  const assignClient = getAppStorageItem('AssignClient');
  const selectedProperty = getAppStorageItem('selectedProperty');

  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [locations, setLocations] = useState([]);
  const [properties, setProperties] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);

  const [data, setData] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [cameraWiseData, setCameraWiseData] = useState([]);
  const [showChart, setShowChart] = useState(false);
  const [noData, setNoData] = useState(false);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(false);
  const [isMonthSelected, setIsMonthSelected] = useState(false);
  const [isYearSelected, setIsYearSelected] = useState(false);
 
  const availableLocations = properties.length > 0 ? properties : locations;
  const showLocationDropdown = availableLocations.length > 1;
  const showGroupDropdown = groups.length > 0;
  const compactLayout = showLocationDropdown && showGroupDropdown;

  const selectedLocationOption = useMemo(
    () => availableLocations.find((loc) => selectedLocationIds.includes(loc.ID || loc.id)) || null,
    [availableLocations, selectedLocationIds]
  );

  const resetReportState = () => {
    setShowChart(false);
    setNoData(false);
    setData([]);
    setGraphData([]);
    setCameraWiseData([]);
    setError(false);
  };

  const loadGroups = (selectedLocationIds = []) => {
    const targetClientId = selectedLocationIds.length > 0 ? selectedLocationIds.join(',') : currentClientId;

    if (!targetClientId) {
      setGroups([]);
      setSelectedGroup(null);
      return;
    }

    getClientGroupInfo({ clientId: targetClientId, groupId: 0 }, (response) => {
      if (response.status === 200 && response.data.length > 0) {
        const uniqueGroups = Array.from(
          new Map(
            response.data.map((item) => [
              item.GroupId,
              { Id: item.GroupId, GroupName: item.GroupName },
            ])
          ).values()
        );
        setGroups(uniqueGroups);
      } else {
        setGroups([]);
        setSelectedGroup(null);
      }
    });
  };

  useEffect(() => {
    if (!currentClientId) return;

    if (assignClient !== '' && storedProperty !== '') {
      const assignedLocations = getAssignedLocationsFromStorage();
      setProperties(assignedLocations);
      setLocations([]);
      setGroups([]);
      setSelectedGroup(null);

      if (assignedLocations.length > 0) {
        const storedLocationId = Number(selectedProperty || 0);
        const matchedLocation = assignedLocations.find((loc) => loc.ID === storedLocationId);
        const defaultLocation = matchedLocation || assignedLocations[0];

        setSelectedLocationIds([defaultLocation.ID]);
        localStorage.setItem('selectedProperty', String(defaultLocation.ID));
        loadGroups([defaultLocation.ID]);
      } else {
        setSelectedLocationIds([]);
        loadGroups([]);
      }

      return;
    }

    getClientProperties({ AssignClient: String(currentClientId) }, (response) => {
      if (response.status === 200) {
        const normalizedLocations = Array.from(
          new Map(
            (response.data || [])
              .map((location) => ({
                ...location,
                ID: Number(location?.ID ?? location?.id ?? 0),
                Location: String(location?.Location || location?.name || '').trim(),
              }))
              .filter((location) => location.ID > 0)
              .map((location) => [location.ID, location])
          ).values()
        );

        setLocations(normalizedLocations);
        setProperties([]);
        setGroups([]);
        setSelectedGroup(null);

        if (normalizedLocations.length > 0) {
          const storedLocationId = Number(selectedProperty || 0);
          const matchedLocation = normalizedLocations.find((loc) => loc.ID === storedLocationId);
          const defaultLocation = matchedLocation || normalizedLocations[0];

          setSelectedLocationIds([defaultLocation.ID]);
          localStorage.setItem('selectedProperty', String(defaultLocation.ID));
          loadGroups([defaultLocation.ID]);
        } else {
          setSelectedLocationIds([]);
          loadGroups([]);
        }
      } else {
        setLocations([]);
        setProperties([]);
        loadGroups([]);
      }
    });
  }, [assignClient, currentClientId, selectedProperty, storedProperty]);

  const handleLocationChange = (event, value) => {
    const nextSelectedLocations = value ? [value.ID || value.id] : [];

    setSelectedLocationIds(nextSelectedLocations);
    setSelectedGroup(null);
    resetReportState();

    if (nextSelectedLocations.length > 0) {
      localStorage.setItem('selectedProperty', String(nextSelectedLocations[0]));
      loadGroups(nextSelectedLocations);
      return;
    }

    setGroups([]);
  };

  const handleGenerate = () => {
    let hasError = false;
    setError(false);

    if (!selectedMonth) {
      setIsMonthSelected(true);
      hasError = true;
    }

    if (!selectedYear) {
      setIsYearSelected(true);
      hasError = true;
    }

    if (hasError) {
      setError(true);
      return;
    }

    const effectiveClientId = selectedLocationIds.length > 0
      ? selectedLocationIds.join(',')
      : availableLocations.length > 0
        ? availableLocations.map((location) => Number(location.ID || location.id)).join(',')
        : currentClientId;

    const body = {
      month: selectedMonth.value,
      year: selectedYear.value,
      client_id: effectiveClientId,
      //clientId: currentClientId,
      group_id: selectedGroup ? Number(selectedGroup.Id || selectedGroup.GroupId || 0) : 0,
    };

    setLoading(true);
    monthlyClientDashboard(body, (response) => {
      if (response.status === 200) {
        if (response.data.length > 0) {
          const apiData = response.data[0];
          setData(response.data);
          setGraphData(apiData.Details || []);
          setCameraWiseData(apiData.camera_wise_counts || []);
          setShowChart(true);
          setNoData(false);
        } else {
          setData([]);
          setGraphData([]);
          setCameraWiseData([]);
          setShowChart(false);
          setNoData(true);
        }
      }

      setLoading(false);
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        {showLocationDropdown && (
          <Grid item xs={12} sm={compactLayout ? 3 : 4}>
            <Autocomplete
              fullWidth
              options={availableLocations}
              value={selectedLocationOption}
              getOptionLabel={(option) => `${option.Location || option.name || ''}`}
              onChange={handleLocationChange}
              isOptionEqualToValue={(option, value) =>
                Number(option.ID || option.id) === Number(value.ID || value.id)
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Location" variant="outlined" />
              )}
            />
          </Grid>
        )}

        {showGroupDropdown && (
          <Grid item xs={12} sm={compactLayout ? 3 : 4}>
            <Autocomplete
              value={selectedGroup}
              onChange={(event, newValue) => {
                setSelectedGroup(newValue);
                resetReportState();
              }}
              options={groups}
              getOptionLabel={(option) => option.GroupName || ''}
              isOptionEqualToValue={(option, value) => Number(option.Id) === Number(value.Id)}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Group" variant="outlined" />
              )}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={showLocationDropdown || showGroupDropdown ? 3 : 4}>
          <Autocomplete
            value={selectedMonth}
            onChange={(event, newValue) => {
              setSelectedMonth(newValue);
              setIsMonthSelected(false);
              resetReportState();
            }}
            options={months}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Month"
                variant="outlined"
                error={error && isMonthSelected}
              />
            )}
          />
          {error && isMonthSelected && (
            <Typography variant="caption" color="error">
              Select Month
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={showLocationDropdown || showGroupDropdown ? 2 : 4}>
          <Autocomplete
            value={selectedYear}
            onChange={(event, newValue) => {
              setSelectedYear(newValue);
              setIsYearSelected(false);
              resetReportState();
            }}
            options={years}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Year"
                variant="outlined"
                error={error && isYearSelected}
              />
            )}
          />
          {error && isYearSelected && (
            <Typography variant="caption" color="error">
              Select Year
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={2}>
          <Button variant="contained" onClick={handleGenerate}>
            Generate
          </Button>
        </Grid>
      </Grid>

      {showChart && (
        <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <FormComponent
            barGraphData={graphData}
            cameraWiseData={cameraWiseData}
            Data={data}
            Month={selectedMonth.name}
            Year={selectedYear.value}
            clientName={currentClientName}
            clientId={currentClientId}
            Location={
              selectedLocationIds.length > 0
                ? availableLocations
                    .filter((location) => selectedLocationIds.includes(location.ID || location.id))
                    .map((location) => location.Location)
                    .join(', ')
                : ''
            }
          />
        </Grid>
      )}

      {!loading && noData && (
        <Grid container justifyContent="center">
          <Typography align="center" sx={{ fontWeight: 'bold', py: 2 }}>
            No Data
          </Typography>
        </Grid>
      )}

      {loading && (
        <Grid container justifyContent="center" sx={{ mt: 2 }}>
          <CircularProgress color="primary" />
        </Grid>
      )}
    </Box>
  );
}
